import React, { cloneElement, useRef, useState } from 'react';
import { cx, useDismiss } from './hooks';

/**
 * Menu that opens from a trigger element.
 *
 * items: [{ key, label, icon, onClick, danger, disabled }] or { type: 'divider' }
 * onItemClick(key): called for every item click (in addition to item.onClick)
 * placement: 'bottomRight' (default) | 'bottomLeft' | 'topRight' | 'topLeft'
 * trigger: 'click' (default) | 'hover'
 */
const Dropdown = ({
  items = [],
  onItemClick,
  placement = 'bottomRight',
  trigger = 'click',
  children,
  className,
  menuClassName,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const closeTimer = useRef(null);

  useDismiss([rootRef], open, () => setOpen(false));

  const hoverProps =
    trigger === 'hover'
      ? {
          onMouseEnter: () => {
            clearTimeout(closeTimer.current);
            setOpen(true);
          },
          onMouseLeave: () => {
            closeTimer.current = setTimeout(() => setOpen(false), 150);
          },
        }
      : {};

  const triggerEl = cloneElement(children, {
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    onClick: e => {
      children.props.onClick?.(e);
      setOpen(o => !o);
    },
  });

  const handleItem = item => {
    if (item.disabled) return;
    setOpen(false);
    item.onClick?.({ key: item.key });
    onItemClick?.(item.key);
  };

  return (
    <div ref={rootRef} className={cx('dropdown', className)} {...hoverProps}>
      {triggerEl}
      {open && (
        <div
          className={cx(
            'dropdown-menu',
            placement.endsWith('Left') && 'is-left',
            placement.startsWith('top') && 'is-top',
            menuClassName
          )}
          role="menu"
        >
          {items.filter(Boolean).map((item, index) =>
            item.type === 'divider' ? (
              <div key={item.key || `divider-${index}`} className="dropdown-divider" role="separator" />
            ) : (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={cx('dropdown-item', item.danger && 'is-danger')}
                disabled={item.disabled}
                onClick={() => handleItem(item)}
              >
                {item.icon}
                {item.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
